import type { RealtimeEntityPropertyValue } from '@interfaces/realtimeEntities';

/**
 * Whether a colour tuple is on the 0-255 `FColor` scale rather than the 0-1
 * `FLinearColor` one.
 *
 * The build's schema says only `color`, so the scale has to be read off the
 * value: any channel above 1 can only be a byte. A property with nothing
 * stored yet reads as linear, which is why the per-channel numbers stay on
 * screen beside the picker — a wrong guess is then visible instead of silent.
 *
 * @param stored - The value currently held for the property, possibly absent.
 * @returns True when the channels are bytes.
 */
export const isByteScaleColor = (
  stored: RealtimeEntityPropertyValue | undefined,
): boolean =>
  Array.isArray(stored) && stored.some((component) => component > 1);

/**
 * Renders a colour tuple as the `#rrggbb` the picker speaks.
 *
 * @param tuple - Channels in the property's own scale; alpha is ignored.
 * @param byteScale - True when the channels are 0-255 rather than 0-1.
 * @returns A six-digit hex colour.
 */
export const colorTupleToHex = (
  tuple: number[],
  byteScale: boolean,
): string => {
  const toByte = (value: number): number => {
    const channel = Number.isFinite(value) ? value : 0;
    const bounded = Math.min(byteScale ? 255 : 1, Math.max(0, channel));

    return Math.round(byteScale ? bounded : bounded * 255);
  };

  return `#${[0, 1, 2]
    .map((axis) =>
      toByte(tuple[axis] ?? 0)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
};

/**
 * Reads `#rrggbb` back into the scale the property is stored in.
 *
 * @param hex - Six-digit hex colour from the picker.
 * @param byteScale - True when the channels are 0-255 rather than 0-1.
 * @returns The three channels; alpha is the caller's to carry over.
 */
export const hexToColorChannels = (hex: string, byteScale: boolean): number[] =>
  [1, 3, 5].map((offset) => {
    const byte = parseInt(hex.slice(offset, offset + 2), 16);
    const channel = Number.isFinite(byte) ? byte : 0;

    // Four decimals resolve finer than an 8-bit channel, so a round-trip
    // through the picker never drifts the stored value.
    return byteScale ? channel : Number((channel / 255).toFixed(4));
  });
